import React, { useEffect, useState } from "react";
import { PlusIcon, MenuVerticalIcon } from "~/assets/icons";
import styles from "./CRM.module.scss";
import classNames from "classnames/bind";
import BaseSelectButton from "~/components/common/base-select-button/BaseSelectButton";
import {
  Calendar,
  ChevronDown,
  Clock,
  Star,
  Tag,
  TrendingUp,
  User,
} from "lucide-react";
import SortButton from "~/components/common/base-sort/BaseSort";
import BaseFilter from "~/components/common/base-filter/BaseFilter";
import BaseTableV2 from "~/components/common/base-table-v2/BaseTableV2";
import AddNewLeadModal from "./add-new-lead/AddNewLeadModal";
import EditCustomerModal from "./edit-customer/EditCustomerModal";
import { useAppDispatch, useAppSelector } from "~/redux/hooks";
import { crmActions } from "~/thunks/crm/crmSlice";
import {
  handleGetCRMListView,
} from "~/thunks/crm/crmThunk";
import { notify } from "~/utils/helper";
import { ICRMListView, IClient } from "~/utils/interface/crm";
import { HeadCelType } from "~/utils/types/common";
import { ROWS_PER_PAGE } from "~/utils/constants/common";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { Menu, MenuItem } from "@mui/material";
import ModalAlertDelete from "~/components/common/modal-alert-delete/ModalAlertDelete";
import crmApi from "~/apis/crm";
const cx = classNames.bind(styles);

const CRM = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const viewOptions = [
    {
      id: "kanban",
      label: "Kanban View",
      icon: (props: { size?: number }) => <ChevronDown size={props.size} />,
    },
    {
      id: "list",
      label: "List View",
      icon: (props: { size?: number }) => <ChevronDown size={props.size} />,
    },
  ];

  const sortOptions = [
    // Date sorting
    {
      field: "createdAt",
      direction: "desc",
      label: "Newest First",
      icon: Calendar,
    },
    {
      field: "createdAt",
      direction: "asc",
      label: "Oldest First",
      icon: Calendar,
    },

    // Name sorting
    { field: "name", direction: "asc", label: "Name A-Z", icon: User },
    { field: "name", direction: "desc", label: "Name Z-A", icon: User },

    // Priority sorting
    {
      field: "priority",
      direction: "desc",
      label: "High Priority First",
      icon: Star,
    },
    {
      field: "priority",
      direction: "asc",
      label: "Low Priority First",
      icon: Star,
    },

    // Status sorting
    { field: "status", direction: "asc", label: "Status A-Z", icon: Tag },
    { field: "status", direction: "desc", label: "Status Z-A", icon: Tag },

    // Due date sorting
    {
      field: "dueDate",
      direction: "asc",
      label: "Due Date (Earliest)",
      icon: Clock,
    },
    {
      field: "dueDate",
      direction: "desc",
      label: "Due Date (Latest)",
      icon: Clock,
    },

    // Progress sorting
    {
      field: "progress",
      direction: "desc",
      label: "Progress (High-Low)",
      icon: TrendingUp,
    },
    {
      field: "progress",
      direction: "asc",
      label: "Progress (Low-High)",
      icon: TrendingUp,
    },
  ];

  const filterOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
    { value: "pending", label: "Pending" },
    { value: "archived", label: "Archived" },
  ];

  const [currentView, setCurrentView] = useState("list");
  const [currentSort, setCurrentSort] = useState(null);
  const [currentFilter, setCurrentFilter] = useState<string[]>([]);
  const [showAddLead, setShowAddLead] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedClient, setSelectedClient] = useState<ICRMListView | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { crmListViews, currentPage, totalPage, totalItems, needRefreshData } =
    useAppSelector((state) => state.crm);

  useEffect(() => {
    return () => {
      dispatch(crmActions.setCurrentPage(1));
    };
  }, [dispatch]);

  useEffect(() => {
    if (needRefreshData || dispatch) {
      dispatch(handleGetCRMListView({ params: { page: currentPage, limit: ROWS_PER_PAGE } }))
        .unwrap()
        .then(() => {
          dispatch(crmActions.changeNeedRefreshData(false));
        })
        .catch((error) => {
          notify("error", `Failed to fetch client: ${error}`);
        });
    }
  }, [needRefreshData, dispatch, currentPage]);

  const handlePageChange = (newPage: number) => {
    dispatch(handleGetCRMListView({ params: { page: newPage, limit: ROWS_PER_PAGE } }))
      .unwrap()
      .catch((error) => {
        notify("error", `Failed to fetch client: ${error}`);
      });
  };

  const handleClickAddLead = async () => {
    setShowAddLead(true);
  };

  const handleCancelAddNewLead = () => {
    setShowAddLead(false);
  };

  const handleSubmitAddNewLead = async (data: any) => {
    // AddNewLeadModal now handles API call internally
    // Just refresh the list after customer is created
    dispatch(crmActions.changeNeedRefreshData(true));
  };

  const handleRowClick = (client: ICRMListView) => {
    navigate(`/admin/crm/detail/${client.id}`);
  };

  const handleActionClick = (event: React.MouseEvent<HTMLElement>, client: ICRMListView) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedClient(client);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDetailClick = () => {
    if (selectedClient) {
      navigate(`/admin/crm/detail/${selectedClient.id}`);
    }
    setAnchorEl(null);
    setSelectedClient(null);
  };

  const handleEditClick = () => {
    setShowEditModal(true);
    setAnchorEl(null);
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
    setAnchorEl(null);
  };

  const handleCancelEdit = () => {
    setShowEditModal(false);
    setSelectedClient(null);
  };

  const handleSubmitEdit = () => {
    setShowEditModal(false);
    setSelectedClient(null);
    dispatch(crmActions.changeNeedRefreshData(true));
  };

  const handleConfirmDelete = async () => {
    if (selectedClient) {
      try {
        await crmApi.deleteCustomer(selectedClient.id);
        notify("success", "Customer deleted successfully!");
        setShowDeleteModal(false);
        setSelectedClient(null);
        dispatch(crmActions.changeNeedRefreshData(true));
      } catch (error: any) {
        notify("error", `Failed to delete customer: ${error?.response?.data?.message || error?.message || "Unknown error"}`);
      }
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setSelectedClient(null);
  };

  const convertToIClient = (client: ICRMListView): IClient => {
    return {
      id: client.id,
      name: client.name || "",
      email: client.email || "",
      phoneNumber: client.phoneNumber || "",
      address: client.address || "",
    };
  };

  const headCells: Array<HeadCelType> = [
    { id: "clientName", label: "Customer Name", width: "15%" },
    { id: "contactMail", label: "Contact Mail", width: "15%" },
    { id: "contactNumber", label: "Contact Number", width: "15%" },
    { id: "address", label: "Address", width: "20%" },
    { id: "addedBy", label: "Added By", width: "20%" },
    { id: "createdDate", label: "Created Date", width: "15%" },
    { id: "action", label: "", width: "5%" },
  ];

  return (
    <div className={cx("crm-container")}>
      <div className={cx("header")}>
       

        <button className={cx("add-button")} onClick={handleClickAddLead}>
          <PlusIcon className="w-[16px] h-[16px] mr-1 [&>path]:fill-white" /> Add Customer
        </button>
      </div>
      <div className={cx("content")}>
        {currentView === "kanban" ? (
          <>
            <h2>Kanban view</h2>
          </>
        ) : (
          <>
            <div className={cx("table-wrapper")}>
              <BaseTableV2
                data={crmListViews}
                headCells={headCells}
                page={currentPage}
                totalPages={totalPage || 1}
                totalItems={totalItems || 0}
                onChangePage={handlePageChange}
                onRowClick={handleRowClick}
                showPagination={true}
                renderCell={(client: ICRMListView, index: number) => (
                  <>
                    <td className={cx("table-body-cell", "w-[15%]")}>
                      <p>{client.name || "-"}</p>
                    </td>
                    <td className={cx("table-body-cell", "w-[15%]")}>
                      <p>{client.email || "-"}</p>
                    </td>
                    <td className={cx("table-body-cell", "w-[15%]")}>
                      <p>{client.phoneNumber || "-"}</p>
                    </td>
                    <td className={cx("table-body-cell", "w-[20%]")}>
                      <p className={cx("address")}>{client.address || "-"}</p>
                    </td>
                    <td className={cx("table-body-cell", "w-[20%]")}>
                      <div className={cx("added-container")}>
                        <div
                          className={cx("color-circle")}
                          style={{
                            backgroundColor: client.addedBy?.color || "#C4C4C4",
                          }}
                        />
                        <p className={cx("added-by-name")}>{client.addedBy?.name || "-"}</p>
                      </div>
                    </td>
                    <td className={cx("table-body-cell", "w-[15%]")}>
                      <p className={cx("due-date")}>
                        {client.createdDate
                          ? `Due: ${dayjs(client.createdDate).format("YYYY-MM-DD")}`
                          : "-"}
                      </p>
                    </td>
                    <td className={cx("table-body-cell", "w-[5%]")}>
                      <button
                        className={cx("action-button", 'mr-4')}
                        onClick={(e) => handleActionClick(e, client)}
                      >
                        <MenuVerticalIcon className={cx("action-icon")} />
                      </button>
                    </td>
                  </>
                )}
              />
            </div>
          </>
        )}
      </div>
      {showAddLead && (
        <AddNewLeadModal
          isOpen={showAddLead}
          onCancel={handleCancelAddNewLead}
          onSubmit={handleSubmitAddNewLead}
        />
      )}

      {showEditModal && selectedClient && (
        <EditCustomerModal
          isOpen={showEditModal}
          onCancel={handleCancelEdit}
          onSubmit={handleSubmitEdit}
          customer={convertToIClient(selectedClient)}
        />
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleDetailClick}>Detail</MenuItem>
        <MenuItem onClick={handleEditClick}>Edit</MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: '#FF2D55' }}>Delete</MenuItem>
      </Menu>

      <ModalAlertDelete
        open={showDeleteModal}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        description={`Are you sure you want to delete customer "${selectedClient?.name || ''}"?`}
      />
    </div>
  );
};

export default CRM;